import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import config from '../config/graphqlConfig';

let onAuthError: (() => void) | null = null;

export function setOnAuthError(callback: () => void): void {
  onAuthError = callback;
}

export function triggerAuthError(): void {
  if (onAuthError) {
    onAuthError();
  } else {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  }
}

const errorLink = onError(({ graphQLErrors, networkError, operation }: any) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error: any) => {
      const { message, locations, path, extensions, errorType } = error;
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, ErrorType: ${errorType}`
      );
      
      const isAuthError = 
        extensions?.code === 'UNAUTHENTICATED' || 
        extensions?.code === 'UNAUTHORIZED' ||
        errorType === 'UnauthorizedException' ||
        message?.includes('not authorized');
      
      if (isAuthError) {
        console.error('Authentication failed');
        if (onAuthError) {
          onAuthError();
        } else {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
        }
      }
      
      const isForbidden = 
        extensions?.code === 'FORBIDDEN' || 
        errorType === 'ForbiddenError';
      
      if (isForbidden) {
        console.error('Access denied - insufficient permissions');
        alert('You do not have permission to perform this action.');
      }
    });
  }
  
  if (networkError) {
    console.error(`[Network error]:`, networkError);
    
    const statusCode = 'statusCode' in networkError ? (networkError as any).statusCode : null;
    const hasToken = !!localStorage.getItem('access_token');
    const result = (networkError as any).result;
    
    const isNetworkAuthError = 
      statusCode === 401 || 
      statusCode === 403 || 
      !hasToken ||
      result?.errors?.some((e: any) => 
        e.errorType === 'UnauthorizedException' || 
        e.message?.includes('not authorized')
      );
    
    if (isNetworkAuthError) {
      console.error('Unauthorized - token expired or missing');
      if (onAuthError) {
        onAuthError();
      } else {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
      }
      return;
    }
    
    if (networkError.message === 'Failed to fetch' || networkError.message?.includes('NetworkError')) {
      console.error('Network connection failed');
      alert('Network error. Please check your internet connection.');
    }
  }
});

// HTTP link with GraphQL endpoint from config and timeout handling
const httpLink = new HttpLink({
  uri: config.graphqlEndpoint,
  headers: {
    'Content-Type': 'application/json',
  },
  fetch: (uri, options) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
    }, 10000); // 10 second timeout

    return fetch(uri, {
      ...options,
      signal: controller.signal,
    }).finally(() => {
      clearTimeout(timeout);
    });
  },
});

// Auth middleware to add token to requests
const authLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('authToken');
  
  if (!token) {
    console.warn('No auth token found - triggering session expired');
    if (onAuthError) {
      setTimeout(() => onAuthError!(), 0);
    }
  }
  
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    }
  }));

  return forward(operation);
});

// Create and export Apollo Client instance
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          rules: {
            // Customize cache behavior for rules query if needed
            merge(existing = [], incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Auth helper functions
export function setAuthToken(token: string): void {
  localStorage.setItem('authToken', token);
}

export function clearAuthToken(): void {
  localStorage.removeItem('authToken');
}

export function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

export default apolloClient;

